import { cloneElement, isValidElement, Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react';
import { stanAnalytics } from '@/lib';
import styles from '../Menu/StanDropdownMenu.module.css'; // Reusing the same styles
import { StanFloatingMenuProps } from '../../types';
import { useUser } from '@/queries/useUser';
import { useFloatingMenu } from '@/hooks/useFloatingTransition';

export interface StanFloatingMenuHandle {
    setMenu: (isOpen: boolean) => void;
}

export const StanFloatingMenu = ({
    id,
    name,
    children,
    trigger,
    className = '',
    placement = 'bottom-end',
    offset: offsetValue = 8,
    minWidth = 180,
    matchTriggerWidth = false,
    keepOpen = false,
    rounded = false,
    disabled = false,
    onOpenChange,
    transitionOptions = {
        scaleAmount: 0.95,
        slideDistance: 10,
        duration: 150,
        transformOrigin: 'top',
        slideDirection: 'bottom',
    },
    ref,
}: StanFloatingMenuProps & { ref?: Ref<StanFloatingMenuHandle> }) => {
    const { user } = useUser();
    const hasBeenOpenedRef = useRef(false);

    // State to track trigger width for matchTriggerWidth functionality
    const [triggerWidth, setTriggerWidth] = useState<number>();

    // Use our comprehensive floating menu hook
    const {
        isOpen,
        setIsOpen,
        refs,
        floatingStyles,
        context,
        getReferenceProps,
        getFloatingProps,
        transitionStyles,
        closeMenu,
    } = useFloatingMenu({
        placement,
        offsetValue,
        keepOpen,
        duration: transitionOptions.duration,
        slideDistance: transitionOptions.slideDistance,
        scaleAmount: transitionOptions.scaleAmount,
        slideDirection: transitionOptions.slideDirection,
        transformOrigin: transitionOptions.transformOrigin,
        name,
        id,
    });

    // Measure trigger width when matchTriggerWidth is true
    useEffect(() => {
        if (!matchTriggerWidth || !isOpen) return;

        const triggerEl = refs.reference.current;
        if (triggerEl) {
            const rect = triggerEl.getBoundingClientRect();
            setTriggerWidth(rect.width);
        }
    }, [isOpen, matchTriggerWidth, refs.reference]);

    useImperativeHandle(ref, () => ({ setMenu: handleSetIsOpen }) as StanFloatingMenuHandle);

    // Custom setIsOpen that fires analytics when state actually changes
    const handleSetIsOpen = (newIsOpen: boolean) => {
        // Only fire analytics after the menu has been opened at least once
        if (name && hasBeenOpenedRef.current) {
            stanAnalytics(`admin-${newIsOpen ? 'show' : 'hide'}-floating-menu`, {
                meta: { user_id: user?.user_id },
                props: {
                    floating_menu_name: name,
                    floating_menu_id: id,
                },
            });
        }

        // Track if menu has been opened at least once
        if (newIsOpen) {
            hasBeenOpenedRef.current = true;
        }

        setIsOpen(newIsOpen);
    };

    // Handle onOpenChange callback
    useEffect(() => {
        onOpenChange?.(isOpen);
    }, [isOpen, onOpenChange]);

    // Expose the close function and isOpen state to child components
    const menuWithProps = typeof children === 'function' ? children({ closeMenu, isOpen }) : children;

    // Create trigger element with proper props
    const triggerElement =
        trigger && isValidElement(trigger)
            ? cloneElement(trigger as React.ReactElement<any>, {
                  ...(disabled ? {} : getReferenceProps()),
                  ref: refs.setReference,
              })
            : null;

    return (
        <>
            {triggerElement}
            {isOpen && (
                <FloatingPortal>
                    <FloatingFocusManager context={context} modal={false}>
                        <div
                            ref={refs.setFloating}
                            style={{
                                ...transitionStyles,
                                ...floatingStyles,
                                minWidth: matchTriggerWidth && triggerWidth ? `${triggerWidth}px` : `${minWidth}px`,
                                width: matchTriggerWidth && triggerWidth ? `${triggerWidth}px` : undefined,
                            }}
                            {...getFloatingProps()}
                            className={`${styles.dropdown} ${className} ${rounded ? styles.rounded : ''}`}
                        >
                            <div className={styles.list}>{menuWithProps}</div>
                        </div>
                    </FloatingFocusManager>
                </FloatingPortal>
            )}
        </>
    );
};
